"use client";

import { useSearchParams, useRouter } from "next/navigation";
import Link from "next/link";
import { BookOpen } from "lucide-react";
import type { GuideFrontmatter } from "@/lib/guides";

interface GuideItem {
  slug: string;
  frontmatter: GuideFrontmatter;
}

interface Props {
  guides: GuideItem[];
  categories: string[];
}

function GuideThumbnail({ guide }: { guide: GuideItem }) {
  if (guide.frontmatter.thumbnail) {
    return (
      <div className="aspect-[16/9] relative overflow-hidden">
        <img
          src={guide.frontmatter.thumbnail}
          alt={guide.frontmatter.title}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className="aspect-[16/9] bg-gradient-to-br from-primary/5 via-primary/10 to-accent/5 flex items-center justify-center">
      <div className="text-center">
        <span className="text-4xl font-bold text-primary/20 block">
          {guide.frontmatter.category.charAt(0)}
        </span>
        <span className="text-xs text-on-surface-variant/40 mt-1 block">
          {guide.frontmatter.category}
        </span>
      </div>
    </div>
  );
}

function GuideCard({ guide }: { guide: GuideItem }) {
  return (
    <Link
      href={`/guides/${guide.slug}`}
      className="group block bg-surface rounded-lg border border-outline-variant/20 overflow-hidden transition-all duration-200 hover:shadow-[0_12px_32px_rgba(44,26,30,0.08)] hover:border-outline-variant/40"
    >
      <GuideThumbnail guide={guide} />
      <div className="p-4">
        <h3 className="text-sm font-bold text-on-surface leading-snug mb-2 line-clamp-2 group-hover:text-primary transition-colors">
          {guide.frontmatter.title}
        </h3>
        {guide.frontmatter.description && (
          <p className="text-xs text-on-surface-variant leading-relaxed line-clamp-2">
            {guide.frontmatter.description}
          </p>
        )}
      </div>
    </Link>
  );
}

function CategorySection({
  category,
  guides,
}: {
  category: string;
  guides: GuideItem[];
}) {
  return (
    <section className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-5 bg-primary rounded-full" />
        <h2 className="text-lg font-bold text-on-surface">{category}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {guides.map((guide) => (
          <GuideCard key={guide.slug} guide={guide} />
        ))}
      </div>
    </section>
  );
}

export function GuidesListClient({ guides, categories }: Props) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const rawCategory = searchParams.get("category");
  const activeCategory = rawCategory && categories.includes(rawCategory) ? rawCategory : null;

  const handleCategoryClick = (category: string | null) => {
    if (category) {
      router.push(`/guides?category=${encodeURIComponent(category)}`, {
        scroll: false,
      });
    } else {
      router.push("/guides", { scroll: false });
    }
  };

  // カテゴリ別にグループ化する
  const groupedByCategory = categories.reduce<Record<string, GuideItem[]>>(
    (acc, cat) => {
      acc[cat] = guides.filter((g) => g.frontmatter.category === cat);
      return acc;
    },
    {}
  );

  const filteredGuides = activeCategory
    ? guides.filter((g) => g.frontmatter.category === activeCategory)
    : guides;

  return (
    <>
      {/* カテゴリフィルタータブ */}
      <div className="flex flex-wrap gap-2 mb-10 pb-6 border-b border-outline-variant/20">
        <button
          onClick={() => handleCategoryClick(null)}
          className={`text-sm px-5 py-2 rounded-md border transition-all duration-200 ${
            !activeCategory
              ? "bg-primary text-white border-primary shadow-[0_2px_8px_rgba(233,89,98,0.2)]"
              : "bg-surface text-on-surface-variant border-outline-variant/30 hover:border-primary/40 hover:text-primary"
          }`}
        >
          すべて
        </button>
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryClick(category)}
            className={`text-sm px-5 py-2 rounded-md border transition-all duration-200 ${
              activeCategory === category
                ? "bg-primary text-white border-primary shadow-[0_2px_8px_rgba(233,89,98,0.2)]"
                : "bg-surface text-on-surface-variant border-outline-variant/30 hover:border-primary/40 hover:text-primary"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {filteredGuides.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-12 h-12 text-on-surface-variant/30 mx-auto mb-4" />
          <p className="text-on-surface-variant">
            {activeCategory
              ? `「${activeCategory}」の記事はまだありません`
              : "まだ記事がありません"}
          </p>
        </div>
      ) : activeCategory ? (
        /* 特定カテゴリ選択時: フラットなグリッド表示 */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredGuides.map((guide) => (
            <GuideCard key={guide.slug} guide={guide} />
          ))}
        </div>
      ) : (
        /* 「すべて」選択時: カテゴリ別セクションで表示 */
        <>
          {categories.map(
            (category) =>
              groupedByCategory[category]?.length > 0 && (
                <CategorySection
                  key={category}
                  category={category}
                  guides={groupedByCategory[category]}
                />
              )
          )}
        </>
      )}
    </>
  );
}
